import React from 'react';
import { useSessionInfoQuery } from '@/api/useSessionInfoQuery';
import { useSessionStore } from '@/state/sessionStore';

export type UserLogoutProps = {
  className?: string;
  buttonLabel?: string;
  redirectTo?: string;
};

/**
 * Renders a logout form for authenticated users.
 * - Uses sessionStore to determine login state (isGuest) and CSRF token/name.
 * - Maintains Craft's POST form semantics with hidden inputs:
 *   action=users/logout, redirect, and CSRF input.
 */
const UserLogout: React.FC<UserLogoutProps> = ({
  className = 'theme-button block w-full px-3 py-3 text-xl font-bold tracking-wider text-center uppercase',
  buttonLabel = 'Log out',
  redirectTo = '/',
}) => {
  // Ensure session info is fetched so the store has up-to-date csrf & isGuest
  useSessionInfoQuery();

  const { csrfToken, csrfTokenName, isGuest } = useSessionStore();

  // Only render when we are confident the user is authenticated
  const loggedIn = isGuest === false;

  if (!loggedIn) return null;

  // Craft forms typically post back to current URL with hidden inputs
  return (
    <form method="post" acceptCharset="UTF-8">
      {csrfToken && csrfTokenName ? (
        <input type="hidden" name={csrfTokenName} value={csrfToken} />
      ) : null}
      <input type="hidden" name="action" value="users/logout" />
      <input type="hidden" name="redirect" value={redirectTo} />
      <button type="submit" className={className}>
        {buttonLabel}
      </button>
    </form>
  );
};

export default UserLogout;
